import { Fragment } from 'react';
import type { SeedingStandingRow } from '../lib/api';
import { displayClanName } from '../lib/displayName';

// ─────────────────────────────────────────────────────────────────────────────
// StandingsTable — the Pos/Team/P/W/L/Pts standings visual, extracted from
// SeedingRoundsPanel (R2) so R4's DivisionsScoreboardPanel can render the
// EXACT same table once per division rather than a near-duplicate. Purely
// presentational — no hooks, no network calls.
//
// `advancesCount`, when given, draws a single quiet divider row after the
// Nth-ranked team (Lisa's "who's heading to the next stage" ask) — a plain
// typographic caption, never a colour-coded row/border (house doctrine: no
// severity-style highlight strips). Omitted entirely for R2's SEEDING rounds,
// which have no advancement concept.
// ─────────────────────────────────────────────────────────────────────────────

export function StandingsTable({
  standings,
  advancesCount,
}: {
  standings: SeedingStandingRow[];
  advancesCount?: number;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[420px] text-xs">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-ink-meta">
            <th className="w-8 py-1">Pos</th>
            <th className="py-1">Team</th>
            <th className="w-10 py-1 text-center">P</th>
            <th className="w-10 py-1 text-center">W</th>
            <th className="w-10 py-1 text-center">L</th>
            <th className="w-12 py-1 text-right">Pts</th>
          </tr>
        </thead>
        <tbody>
          {standings.map((s, i) => (
            <Fragment key={s.name}>
              <tr className="border-t border-white/[0.05]">
                <td className="py-1.5 text-ink-meta">{i + 1}</td>
                <td className="py-1.5 text-ink-primary">{displayClanName(s.name)}</td>
                <td className="py-1.5 text-center text-ink-secondary">{s.played}</td>
                <td className="py-1.5 text-center text-ink-secondary">{s.wins}</td>
                <td className="py-1.5 text-center text-ink-secondary">{s.losses}</td>
                <td className="py-1.5 text-right font-semibold text-ink-primary">{s.points}</td>
              </tr>
              {advancesCount !== undefined && i === advancesCount - 1 && i < standings.length - 1 && (
                <tr>
                  <td colSpan={6} className="py-1">
                    <div className="flex items-center gap-1.5 text-center text-[10px] uppercase tracking-wide text-ink-meta">
                      <span className="h-px flex-1 bg-white/[0.08]" />
                      <span>Advances to next stage</span>
                      <span className="h-px flex-1 bg-white/[0.08]" />
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
